import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

// Form Data Types
export interface FormFieldData {
  fieldId: string
  variableName: string
  value: string | number | boolean
  label: string
  inputType: string
}

export interface FormSubmission {
  id: string
  formNodeId: string
  sessionId: string
  formName: string
  fields: FormFieldData[]
  submittedAt: Date
  chatTranscript?: string
}

export interface EmailConfiguration {
  smtpHost: string
  smtpPort: number
  smtpSecure: boolean
  smtpUser: string
  smtpPassword: string
  fromEmail: string
  fromName: string
}

// Store State
interface FormDataState {
  // Current form data being collected
  currentFormData: Record<string, FormFieldData>

  // Form submissions history
  submissions: FormSubmission[]

  // Email configuration
  emailConfig: EmailConfiguration | null

  // UI state
  isSubmitting: boolean
  submitError: string | null

  // Actions
  setFieldValue: (fieldId: string, field: FormFieldData) => void
  clearCurrentForm: () => void
  submitForm: (
    formNodeId: string,
    sessionId: string,
    formName: string,
    chatTranscript?: string
  ) => Promise<string>
  addSubmission: (submission: FormSubmission) => void
  setEmailConfig: (config: EmailConfiguration) => void
  setSubmitting: (isSubmitting: boolean) => void
  setSubmitError: (error: string | null) => void

  // Getters
  getCurrentFormFields: () => FormFieldData[]
  getSubmissionById: (id: string) => FormSubmission | undefined
}

const initialState = {
  currentFormData: {},
  submissions: [],
  emailConfig: null,
  isSubmitting: false,
  submitError: null,
}

export const useFormDataStore = create<FormDataState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Set field value in current form
        setFieldValue: (fieldId, field) =>
          set(state => ({
            currentFormData: {
              ...state.currentFormData,
              [fieldId]: field,
            },
          })),

        // Clear current form data
        clearCurrentForm: () => set({ currentFormData: {} }),

        // Submit form data
        submitForm: async (formNodeId, sessionId, formName, chatTranscript) => {
          const state = get()
          const submissionId = `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

          set({ isSubmitting: true, submitError: null })

          try {
            const submission: FormSubmission = {
              id: submissionId,
              formNodeId,
              sessionId,
              formName,
              fields: Object.values(state.currentFormData),
              submittedAt: new Date(),
              chatTranscript,
            }

            // Add to submissions
            set(state => ({
              submissions: [...state.submissions, submission],
              isSubmitting: false,
            }))

            // Clear current form
            get().clearCurrentForm()

            return submissionId
          } catch (error) {
            set({
              isSubmitting: false,
              submitError:
                error instanceof Error
                  ? error.message
                  : 'Failed to submit form',
            })
            throw error
          }
        },

        // Add submission manually
        addSubmission: submission =>
          set(state => ({
            submissions: [...state.submissions, submission],
          })),

        // Set email configuration
        setEmailConfig: config => set({ emailConfig: config }),

        // Set submitting state
        setSubmitting: isSubmitting => set({ isSubmitting }),

        // Set submit error
        setSubmitError: error => set({ submitError: error }),

        // Get current form fields as array
        getCurrentFormFields: () => {
          const state = get()
          return Object.values(state.currentFormData)
        },

        // Get submission by ID
        getSubmissionById: id => {
          const state = get()
          return state.submissions.find(sub => sub.id === id)
        },
      }),
      {
        name: 'form-data-storage',
        partialize: state => ({
          submissions: state.submissions,
          emailConfig: state.emailConfig,
        }),
      }
    ),
    { name: 'FormDataStore' }
  )
)
